import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'

const teal = '#0D9488'
const plum = '#1E1040'
const cream = '#FAF7F2'

export default function Privacy() {
  useEffect(() => { window.scrollTo(0, 0) }, [])

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <Link to="/" style={styles.back}>← Back</Link>
        <h1 style={styles.h1}>Privacy Policy</h1>
        <p style={styles.effective}>Last updated: April 2026</p>

        <p style={styles.lead}>
          Your privacy matters. This policy explains what data we collect, why we collect it,
          and what we do (and don't do) with it. Plain English on purpose.
        </p>

        <h2 style={styles.h2}>Who We Are</h2>
        <p style={styles.p}>
          Merq Log is operated by Merq Software, a sole proprietorship based in Texas, United States.
          When this policy says "we," "us," or "our," it means Merq Software.
        </p>

        <h2 style={styles.h2}>What We Collect</h2>

        <h3 style={styles.h3}>Information you give us</h3>
        <ul style={styles.ul}>
          <li><strong>Account info:</strong> name, email address, and a hashed password when you sign up</li>
          <li><strong>Receipts:</strong> images you upload, plus extracted details (merchant, amount, date, category)</li>
          <li><strong>Mileage trips:</strong> origin, destination, distance, date, purpose, and vehicle</li>
          <li><strong>Vehicles:</strong> label, make, model, year, and odometer readings if you choose to enter them</li>
          <li><strong>Tags, payment methods, and categories</strong> you create</li>
          <li><strong>Subscription details:</strong> Paddle handles payment processing as our merchant of record. We do not store credit card numbers.</li>
        </ul>

        <h3 style={styles.h3}>Information we collect automatically</h3>
        <ul style={styles.ul}>
          <li>Login times, feature usage, and basic error logs</li>
          <li>Browser type, operating system, and IP address</li>
          <li>Session tokens, language preference, and UI state via cookies and local storage</li>
        </ul>

        <h2 style={styles.h2}>What We Do With It</h2>
        <p style={styles.p}>We use your data to:</p>
        <ul style={styles.ul}>
          <li>Provide and operate the app (process receipts, calculate mileage, generate reports)</li>
          <li>Process subscriptions and payments via Paddle</li>
          <li>Send you transactional emails (confirmations, password resets, renewal reminders)</li>
          <li>Improve the app and develop new features</li>
          <li>Detect and prevent fraud or abuse</li>
          <li>Comply with legal obligations</li>
        </ul>
        <p style={styles.p}>
          <strong>We don't sell your data.</strong> We don't share it with advertisers. We don't use
          your receipts or trips to train AI models. We don't read your data for any reason other
          than running the app.
        </p>

        <h2 style={styles.h2}>Service Providers</h2>
        <p style={styles.p}>To run Merq Log, we share information with these providers:</p>
        <ul style={styles.ul}>
          <li><strong>Supabase</strong> — database, authentication, and file storage</li>
          <li><strong>Paddle</strong> — payment processing (merchant of record)</li>
          <li><strong>Vercel</strong> — application hosting and content delivery</li>
          <li><strong>Google Maps</strong> — address autocomplete and distance calculation. Address text and trip endpoints are sent to Google.</li>
          <li><strong>Anthropic</strong> — AI-powered receipt scanning. Receipt images are sent to Anthropic's API to extract merchant, amount, and date. Per Anthropic's policy, this data is not used to train AI models.</li>
        </ul>

        <h2 style={styles.h2}>How Long We Keep Your Data</h2>
        <p style={styles.p}>
          We keep your data for as long as your account is active. If you delete your account,
          we delete your personal data within 30 days, except where we are required to retain it
          for legal, tax, or accounting purposes (typically up to 7 years for tax-related records).
        </p>

        <h2 style={styles.h2}>Your Rights</h2>
        <p style={styles.p}>You can:</p>
        <ul style={styles.ul}>
          <li>Access the data we hold about you</li>
          <li>Correct anything that's wrong</li>
          <li>Delete your account ("right to be forgotten")</li>
          <li>Export your data</li>
          <li>Object to or restrict processing</li>
          <li>Withdraw consent at any time</li>
        </ul>
        <p style={styles.p}>
          To exercise any of these rights, email us at <a href="mailto:legal@getmerq.com" style={styles.link}>legal@getmerq.com</a>.
          We respond within 30 days.
        </p>

        <p style={styles.p}>
          Merq Log is built primarily for self-employed people and freelancers in the United States.
          Wherever you live, you have the rights above. Residents of California (under CCPA), the
          European Union (under GDPR), and the United Kingdom have specific statutory rights, all
          of which our practices respect. To exercise any right, email{' '}
          <a href="mailto:legal@getmerq.com" style={styles.link}>legal@getmerq.com</a>. We don't sell personal information.
        </p>

        <h2 style={styles.h2}>Security</h2>
        <p style={styles.p}>
          We use industry-standard security: HTTPS encryption in transit, encryption at rest,
          row-level security policies on our database, and hashed passwords. No system is 100%
          secure. If we ever become aware of a data breach affecting your account, we'll notify
          you within 72 hours.
        </p>

        <h2 style={styles.h2}>Children</h2>
        <p style={styles.p}>
          Merq Log is not for children under 13. We don't knowingly collect personal information
          from children under 13. If you believe we have, email <a href="mailto:legal@getmerq.com" style={styles.link}>legal@getmerq.com</a>{' '}
          and we'll delete it.
        </p>

        <h2 style={styles.h2}>International Data Transfers</h2>
        <p style={styles.p}>
          Our service providers may store data in the United States and other countries. By using
          Merq Log, you consent to your data being transferred to and processed in these countries.
        </p>

        <h2 style={styles.h2}>Changes to This Policy</h2>
        <p style={styles.p}>
          We may update this policy from time to time. When we do, we'll update the "Last updated"
          date at the top of this page. We'll notify you of material changes by email or by posting
          a prominent notice in the app before the change takes effect.
        </p>

        <h2 style={styles.h2}>Contact Us</h2>
        <p style={styles.p}>
          Questions about privacy? Email <a href="mailto:legal@getmerq.com" style={styles.link}>legal@getmerq.com</a>.
        </p>
        <div style={styles.footer}>
          <Link to="/terms" style={styles.footerLink}>Terms of Service</Link>
          <Link to="/refunds" style={styles.footerLink}>Refund Policy</Link>
          <Link to="/contact" style={styles.footerLink}>Contact</Link>
        </div>
      </div>
    </div>
  )
}

const styles = {
  page: { minHeight: '100vh', background: cream, fontFamily: "'Plus Jakarta Sans', sans-serif", color: plum, padding: '40px 20px 80px' },
  container: { maxWidth: '760px', margin: '0 auto' },
  back: { color: teal, textDecoration: 'none', fontSize: '14px', fontWeight: 600 },
  h1: { fontFamily: "'Fraunces', serif", fontSize: '40px', fontWeight: 900, letterSpacing: '-1px', margin: '20px 0 8px 0' },
  effective: { fontSize: '13px', color: '#6B7280', fontWeight: 500, marginBottom: '32px' },
  lead: { fontSize: '16px', lineHeight: 1.7, marginBottom: '32px', color: '#374151' },
  h2: { fontFamily: "'Fraunces', serif", fontSize: '24px', fontWeight: 800, letterSpacing: '-0.4px', marginTop: '40px', marginBottom: '14px' },
  h3: { fontSize: '17px', fontWeight: 700, marginTop: '22px', marginBottom: '10px', color: plum },
  p: { fontSize: '15px', lineHeight: 1.65, color: '#374151', marginBottom: '14px' },
  ul: { fontSize: '15px', lineHeight: 1.7, color: '#374151', paddingLeft: '20px', marginBottom: '14px' },
  link: { color: teal, fontWeight: 600 },
  address: { fontSize: '14px', lineHeight: 1.7, color: '#374151', background: '#fff', padding: '14px 18px', borderRadius: '10px', border: '1px solid #E5E7EB' },
  footer: { marginTop: '60px', paddingTop: '24px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: '20px', flexWrap: 'wrap' },
  footerLink: { color: teal, textDecoration: 'none', fontSize: '13px', fontWeight: 600 },
}
